import { CommandExecutionContext, SelfContainedCommand } from 'types/commands';
import { StandardToolResult } from 'types/standardToolResults';
import { API_BASE_URL } from '../../../../api/config';
import { createFileLogger } from '../../../../utils/logger';

const log = createFileLogger('START_MANUSCRIPT_ANALYSIS');

/**
 * Background polling function to check when analysis is complete and open the manuscript
 * Uses exponential backoff to efficiently poll for long-running analyses
 */
async function pollForCompletionAndOpenManuscript(
  report_id: string,
  manuscript_doc_id: string,
  authToken: string
): Promise<void> {
  log.log('[pollForCompletion] Starting background polling with exponential backoff:', { 
    report_id, 
    manuscript_doc_id 
  });
  
  let attempts = 0;
  
  /**
   * Get polling interval based on elapsed time
   * - First 2 minutes (24 attempts): every 5s
   * - Next 8 minutes (48 attempts): every 10s  
   * - Next 60 minutes (120 attempts): every 30s
   * - After 70 minutes: every 60s
   * Max duration: ~16 hours (1000 attempts)
   */
  const getPollingInterval = (attempt: number): number => {
    if (attempt < 24) return 5000;      // 0-2 min: 5s intervals
    if (attempt < 72) return 10000;     // 2-10 min: 10s intervals
    if (attempt < 192) return 30000;    // 10-70 min: 30s intervals
    return 60000;                        // 70+ min: 60s intervals
  };
  
  const poll = async () => {
    attempts++;
    const interval = getPollingInterval(attempts);
    
    try {
      // Fetch the report entity to check status
      const { entityService } = await import('../../../../services/entityService');
      const reportEntity = await entityService.get(report_id);
      
      if (!reportEntity) {
        log.warn('[pollForCompletion] Report entity not found, stopping poll');
        return;
      }
      
      const state = reportEntity.skeleton?.state;
      
      // Log less frequently as time goes on to reduce noise
      if (attempts % 10 === 0 || attempts < 20) {
        log.log(`[pollForCompletion] Attempt ${attempts}, state: ${state}, next check in ${interval/1000}s`);
      }
      
      if (state === 'complete') {
        // Analysis is complete! Open the manuscript document
        log.log('[pollForCompletion] ✅ Analysis complete! Opening manuscript:', manuscript_doc_id);
        
        const manuscriptEntity = await entityService.get(manuscript_doc_id);
        
        if (manuscriptEntity) {
          const { useCommandRegistryStore } = await import('../../../../stores/commandRegistryStore');
          const { getWindowSizeAndPosition } = await import('../../../../components/Space/utils/windowUtils');
          
          const { executeCommand } = useCommandRegistryStore.getState();
          const { size, position } = getWindowSizeAndPosition();
          
          // Open the manuscript with annotations
          executeCommand('space_openEntity', {
            entity: manuscriptEntity,
            position: { ...position, x: position.x + 50 }, // Offset slightly from report
            size,
            callback: (openResult: string) => {
              log.log('[pollForCompletion] Manuscript opened:', openResult);
            }
          });
          
          log.log('[pollForCompletion] ✅ Manuscript opened successfully after', attempts, 'attempts');
        } else {
          log.warn('[pollForCompletion] Manuscript entity not found:', manuscript_doc_id);
        }
        
        return; // Stop polling
      } else if (state === 'error' || state === 'failed') {
        log.warn('[pollForCompletion] Analysis failed, stopping poll after', attempts, 'attempts');
        return;
      }
      
      // Continue polling if not complete and haven't exceeded max attempts (1000 = ~16 hours)
      if (attempts < 1000) {
        setTimeout(poll, interval);
      } else {
        log.warn('[pollForCompletion] Max polling attempts (1000) reached after ~16 hours, giving up');
      }
      
    } catch (error) {
      log.error('[pollForCompletion] Error during polling:', error);
      // Continue polling despite errors (might be transient)
      if (attempts < 1000) {
        setTimeout(poll, interval);
      }
    }
  };
  
  // Start polling after a short delay
  setTimeout(poll, 5000);
}

export const startManuscriptAnalysisCommand: SelfContainedCommand = {
  id: 'start_manuscript_analysis',
  description: 'Start deep manuscript analysis after gathering clarifying information from the writer. Use this after completing the Q&A session.',
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',
  
  schema: {
    parameters: [
      {
        name: 'report_id',
        type: 'string',
        description: 'ID of the ManuscriptDiscoveryReport entity',
        required: true,
        examples: ['manuscriptdiscoveryreport_gentle_light_melon']
      },
      {
        name: 'user_answers',
        type: 'string',
        description: "Summary of the writer's answers to clarifying questions",
        required: true,
        examples: ['1a, 2e, 3a, 4b - Auto-detect natural breaks for chapters, Romance genre, focus on Pacing & Structure, revision stage requiring detailed critique']
      },
      {
        name: 'manuscript_doc_id',
        type: 'string',
        description: 'ID of the manuscript Doc entity (will be opened when analysis completes)',
        required: false,
        examples: ['doc_brave_sheep_shell']
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: {
          report_id: 'manuscriptdiscoveryreport_gentle_light_melon',
          user_answers: '1a, 2e, 3a, 4b - Auto-detect chapters, Romance, Pacing focus, revision stage'
        },
        expectedOutput: {
          status: 'success',
          message: 'Analysis started! You\'ll see progress updates in the report entity.',
          format: 'plaintext',
          data: {
            job_id: 'job_id_example',
            status: 'processing'
          }
        },
        description: 'Start analysis with user answers'
      }
    ],
    bad: []
  },
  
  performance: {
    expectedDuration: 2000,
    complexity: 'low',
    memoryIntensive: false
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { report_id, user_answers, manuscript_doc_id } = params;
    
    console.log('🎯 [start_manuscript_analysis] ===== TOOL CALLED =====', {
      report_id,
      user_answers_preview: user_answers?.substring(0, 100),
      manuscript_doc_id,
      timestamp: new Date().toISOString()
    });
    
    log.log('[start_manuscript_analysis] Starting manuscript analysis:', {
      report_id,
      user_answers_preview: user_answers?.substring(0, 100),
      manuscript_doc_id
    });
    
    try {
      // Get auth token
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        return {
          status: 'error',
          message: 'Authentication required',
          format: 'plaintext',
          error: {
            code: 'AUTH_TOKEN_MISSING',
            category: 'permission',
            details: 'No auth token found in localStorage',
            retryable: false
          }
        };
      }
      
      // Build answers object
      const answers = {
        free_form_responses: user_answers,
        questions_asked: ''  // Will be populated by backend from report
      };
      
      // Call backend to submit answers and start analysis
      const response = await fetch(`${API_BASE_URL}/manuscript-feedback/v2/submit-answers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        credentials: 'include',
        body: JSON.stringify({
          report_id,
          answers
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        return {
          status: 'error',
          message: errorData.detail || 'Failed to start analysis',
          format: 'plaintext',
          error: {
            code: 'API_ERROR',
            category: 'execution',
            details: errorData,
            retryable: true
          }
        };
      }
      
      const result = await response.json();
      
      log.log('[start_manuscript_analysis] ✅ Analysis started:', result);
      
      // Open the report entity so user can see progress
      try {
        const { entityService } = await import('../../../../services/entityService');
        const reportEntity = await entityService.get(report_id);
        
        if (reportEntity) {
          log.log('[start_manuscript_analysis] Opening report entity:', report_id);
          
          // Import command registry and window utils
          const { useCommandRegistryStore } = await import('../../../../stores/commandRegistryStore');
          const { getWindowSizeAndPosition } = await import('../../../../components/Space/utils/windowUtils');
          
          const { executeCommand } = useCommandRegistryStore.getState();
          const { size, position } = getWindowSizeAndPosition();
          
          // Open the report entity in a new window
          executeCommand('space_openEntity', {
            entity: reportEntity,
            position,
            size,
            callback: (openResult: string) => {
              log.log('[start_manuscript_analysis] Report entity opened:', openResult);
            }
          });
          
          // 🎯 Start background polling to open manuscript when complete
          if (manuscript_doc_id) {
            pollForCompletionAndOpenManuscript(report_id, manuscript_doc_id, authToken);
          }
        }
      } catch (openError) {
        log.warn('[start_manuscript_analysis] Failed to auto-open report:', openError);
        // Don't fail the command if opening fails
      }
      
      return {
        status: 'success',
        message: 'Analysis started! You\'ll see progress updates in the report entity.',
        format: 'plaintext',
        data: {
          job_id: result.job_id,
          status: result.status
        }
      };
      
    } catch (error) {
      log.error('[start_manuscript_analysis] Failed:', error);
      return {
        status: 'error',
        message: error instanceof Error ? error.message : 'Failed to start analysis',
        format: 'plaintext',
        error: {
          code: 'UNEXPECTED_ERROR',
          category: 'system',
          details: error instanceof Error ? error.message : String(error),
          retryable: true
        }
      };
    }
  }
};

